export default function YamlveditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - YAML Viewer And Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-04-25">April 25, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>YAML Viewer And Editor</strong> (yamlvedit) app as an
            ad-supported application. This Privacy Policy explains what
            information yamlvedit may process, how that information is used,
            and the choices you have when using the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>YAML files and source content:</strong> YAML files
              you create, open, or import are parsed and rendered locally
              on your device. They are never uploaded to our servers.
            </li>
            <li>
              <strong>Conversions and exports:</strong> when you convert
              YAML to JSON or XML, or export a formatted file, processing
              runs entirely on your device. The file content never leaves
              your phone.
            </li>
            <li>
              <strong>Saved files:</strong> files you save are written to
              the location you pick in the system save dialog. They are
              not sent anywhere.
            </li>
            <li>
              <strong>App preferences:</strong> theme choice, editor
              settings, recent file history, language, and favourites are
              stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled,
              AdMob or related Google Mobile Ads services may collect
              device and advertising data as described in Google&apos;s
              own privacy documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase
              Analytics and Firebase Crashlytics may collect app
              stability, usage patterns, and crash reports to improve
              reliability and user experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To let you write, edit, validate, and preview YAML files on
              your device.
            </li>
            <li>
              To convert YAML to JSON or XML, and to format and lint
              files, all on your device, offline.
            </li>
            <li>
              To export YAML, JSON, or XML files to a location you choose.
            </li>
            <li>
              To remember your editor settings, recent files, and
              preferences.
            </li>
            <li>
              To improve app performance, reliability, and product
              quality.
            </li>
            <li>
              To display ads in supported versions of the app, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Local Storage
          </h2>
          <p>
            yamlvedit is designed to keep your data on your device. Source
            files, recent file history, template library, and preferences
            are stored locally and are not uploaded to any server. Data
            remains on your device until you remove it, clear the
            app&apos;s storage, or uninstall the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access
          </h2>
          <p>
            Writing, editing, validating, and converting YAML does not
            require network access — the parser, validator, and converters
            are bundled with the app. The app makes network requests only
            for ads, Firebase analytics and crash reporting, the in-app
            update prompt, and the rotating cross-promotion banner shown
            on the home screen. No YAML content or file data is ever
            transmitted.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            yamlvedit may use <strong>Google AdMob</strong> to show
            banner, interstitial, and rewarded ad formats. AdMob may
            collect and process identifiers, device information,
            approximate location, and ad interaction data to deliver and
            measure ads. You can remove ads via a one-time Pro upgrade.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            yamlvedit offers an optional one-time Pro upgrade
            (product&nbsp;id <code>remove_ads</code>) that removes
            advertisements. Purchases are processed through Google Play
            and are subject to Google&apos;s terms and privacy policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>yamlvedit may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
            <li>Google Play In-App Update</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your YAML files, source content, or personal
            data. Data may be shared only with service providers required
            to operate app features, such as advertising, analytics, app
            distribution, or diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local app data remains on your device until you remove it,
            clear the app&apos;s storage, or uninstall the app. Data
            collected by third-party services such as AdMob and Firebase
            is retained according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            yamlvedit is not intended for children under 13, and we do
            not knowingly collect personal information from children
            through the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the
            app, but no method of storage or transmission is completely
            secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>You can delete individual files from inside the app.</li>
            <li>
              You can uninstall the app to remove all locally stored data
              from your device.
            </li>
            <li>
              You can purchase the Pro upgrade to disable all advertising.
            </li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we
            do, we will post the updated version on this page and revise
            the effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about YAML Viewer And Editor,
            contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
